import { CurrentUserPartnerOrganizationListDto, CurrentUserPartnerOrganizationUpgradeEligibilityListDto, MspClientDetailsResponseDto, MspClientListResponseDto, MspResellerDetailsResponseDto, PartnerOrganizationActionsResponseSchema, PartnerOrganizationDirectWorkspacesListResponseDto, PartnerOrganizationListQuery, PartnerOrganizationResponseDto, PartnerOrganizationWorkspacesListQuery, PartnerOrganizationWorkspacesListResponseDto, ResellerListResponseDto, UserResponseDto, VarClientDetailsResponseDto, VarClientListResponseDto, VarResellerDetailsResponseDto, } from '@lucidlink/dto/web-service-api';
import { arrayParserFromDto, parserFromDto } from '#fetcher';
import { remapUserDto, } from '#types/sdk';
import { createPartnerOrganizationApiKeysManager } from './api-keys-manager';
import { createPartnerOrganizationBillingManager } from './billing-manager';
import { createPartnerOrganizationMemberManager } from './member-manager';
import { PartnerOrganizationMigrationManagerImpl } from './migration-manager';
import { PartnerOrganizationOwnershipManagerImpl } from './ownership-manager';
import { WorkspaceMigrationManagerImpl } from './workspace-migration-manager';
export const createPartnerOrganizationsClient = ({ partnerOrganizationsFetcher, }) => new PartnerOrganizationsClientImpl(partnerOrganizationsFetcher);
class PartnerOrganizationsClientImpl {
    partnerOrganizationsFetcher;
    memberManagers = new Map();
    billingManagers = new Map();
    migrationManagers = new Map();
    workspaceMigrationManagers = new Map();
    ownershipManagers = new Map();
    apiKeysManagers = new Map();
    constructor(partnerOrganizationsFetcher) {
        this.partnerOrganizationsFetcher = partnerOrganizationsFetcher;
    }
    apiKeysManagement(partnerOrganizationId) {
        let manager = this.apiKeysManagers.get(partnerOrganizationId);
        if (!manager) {
            manager = createPartnerOrganizationApiKeysManager(this.partnerOrganizationsFetcher.createSubFetcher({
                path: partnerOrganizationId,
            }));
            this.apiKeysManagers.set(partnerOrganizationId, manager);
        }
        return manager;
    }
    memberManagement(partnerOrganizationId) {
        let manager = this.memberManagers.get(partnerOrganizationId);
        if (!manager) {
            manager = createPartnerOrganizationMemberManager({
                partnerOrganizationFetcher: this.partnerOrganizationsFetcher.createSubFetcher({
                    path: partnerOrganizationId,
                }),
            });
            this.memberManagers.set(partnerOrganizationId, manager);
        }
        return manager;
    }
    billingManagement(partnerOrganizationId) {
        let manager = this.billingManagers.get(partnerOrganizationId);
        if (!manager) {
            manager = createPartnerOrganizationBillingManager({
                partnerOrganizationFetcher: this.partnerOrganizationsFetcher.createSubFetcher({
                    path: partnerOrganizationId,
                }),
            });
            this.billingManagers.set(partnerOrganizationId, manager);
        }
        return manager;
    }
    migrationManagement(partnerOrganizationId) {
        let manager = this.migrationManagers.get(partnerOrganizationId);
        if (!manager) {
            manager = new PartnerOrganizationMigrationManagerImpl(this.partnerOrganizationsFetcher.createSubFetcher({
                path: partnerOrganizationId,
            }));
            this.migrationManagers.set(partnerOrganizationId, manager);
        }
        return manager;
    }
    workspaceMigrationManagement(partnerOrganizationId) {
        let manager = this.workspaceMigrationManagers.get(partnerOrganizationId);
        if (!manager) {
            manager = new WorkspaceMigrationManagerImpl(this.partnerOrganizationsFetcher.createSubFetcher({
                path: partnerOrganizationId,
            }));
            this.workspaceMigrationManagers.set(partnerOrganizationId, manager);
        }
        return manager;
    }
    ownershipManagement(partnerOrganizationId) {
        let manager = this.ownershipManagers.get(partnerOrganizationId);
        if (!manager) {
            manager = new PartnerOrganizationOwnershipManagerImpl(this.partnerOrganizationsFetcher.createSubFetcher({
                path: partnerOrganizationId,
            }));
            this.ownershipManagers.set(partnerOrganizationId, manager);
        }
        return manager;
    }
    async getPartnerOrganization(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId], {
            parser: parserFromDto(PartnerOrganizationResponseDto),
        });
    }
    async getMspResellerDetails(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId, 'msp-details'], {
            parser: parserFromDto(MspResellerDetailsResponseDto),
        });
    }
    async getVarResellerDetails(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId, 'var-details'], {
            parser: parserFromDto(VarResellerDetailsResponseDto),
        });
    }
    async getMspClientDetails(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId, 'msp-client-details'], {
            parser: parserFromDto(MspClientDetailsResponseDto),
        });
    }
    async getVarClientDetails(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId, 'var-client-details'], {
            parser: parserFromDto(VarClientDetailsResponseDto),
        });
    }
    async getPartnerOrganizationActions(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId, 'actions'], {
            parser: (json) => PartnerOrganizationActionsResponseSchema.parse(json),
        });
    }
    async listMspClients(parentOrganizationId, params = {}) {
        return this.partnerOrganizationsFetcher.get([parentOrganizationId, 'msp-clients'], {
            queryParams: PartnerOrganizationListQuery.encode(params),
            parser: parserFromDto(MspClientListResponseDto),
        });
    }
    async listVarClients(parentOrganizationId, params = {}) {
        return this.partnerOrganizationsFetcher.get([parentOrganizationId, 'var-clients'], {
            queryParams: PartnerOrganizationListQuery.encode(params),
            parser: parserFromDto(VarClientListResponseDto),
        });
    }
    async createPartnerOrganization(parentId, createPartnerOrganizationDto) {
        return this.partnerOrganizationsFetcher.post([parentId, 'partner-organizations'], {
            body: createPartnerOrganizationDto,
            parser: parserFromDto(PartnerOrganizationResponseDto),
        });
    }
    async listPartnerOrganizations(params = {}) {
        return this.partnerOrganizationsFetcher.get(['partners'], {
            queryParams: PartnerOrganizationListQuery.encode(params),
            parser: parserFromDto(ResellerListResponseDto),
        });
    }
    async updatePartnerOrganization(partnerOrganizationId, updatePartnerOrganizationDto) {
        return this.partnerOrganizationsFetcher.patch([partnerOrganizationId], {
            body: updatePartnerOrganizationDto,
            parser: parserFromDto(PartnerOrganizationResponseDto),
        });
    }
    async updatePartnerOrganizationNotes(partnerOrganizationId, updatePartnerOrganizationNotesDto) {
        return this.partnerOrganizationsFetcher.put([partnerOrganizationId, 'notes'], {
            body: updatePartnerOrganizationNotesDto,
            parser: parserFromDto(PartnerOrganizationResponseDto),
        });
    }
    async getEligibleForWorkspaceCreation() {
        return this.partnerOrganizationsFetcher.get(['me', 'eligible-for-workspace'], {
            parser: parserFromDto(CurrentUserPartnerOrganizationListDto),
        });
    }
    async getUpgradeEligibility() {
        return this.partnerOrganizationsFetcher.get(['me', 'upgrade-eligibility'], {
            parser: parserFromDto(CurrentUserPartnerOrganizationUpgradeEligibilityListDto),
        });
    }
    async getAccountExecutives() {
        const accountExecutivesUsers = await this.partnerOrganizationsFetcher.get(['account-executives'], {
            parser: arrayParserFromDto(UserResponseDto),
        });
        return accountExecutivesUsers.map((user) => remapUserDto(user));
    }
    async listWorkspaces(partnerOrganizationId, params) {
        return this.partnerOrganizationsFetcher.get([partnerOrganizationId, 'workspaces'], {
            parser: parserFromDto(PartnerOrganizationWorkspacesListResponseDto),
            queryParams: PartnerOrganizationWorkspacesListQuery.encode(params),
        });
    }
    async listDirectWorkspaces(clientOrganizationId) {
        return this.partnerOrganizationsFetcher.get([clientOrganizationId, 'workspaces', 'eligible-for-migration'], {
            parser: parserFromDto(PartnerOrganizationDirectWorkspacesListResponseDto),
        });
    }
    async deletePartnerOrganization(partnerOrganizationId) {
        return this.partnerOrganizationsFetcher.delete([partnerOrganizationId]);
    }
}
